/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { Box, Grid, InputLabel } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'

import { Container, Page } from 'components'
import {
  ExportFormatDialog,
  Header,
  ReportVersionTable,
  PartnerForm,
  PartnerAuditForm,
  PartnerReportSelection,
} from './components'

import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import useStyles from './styles'
import constants from 'constants/index'
import schema from './components/PartnerForm/schema'
import helpers from 'helpers'

const PartnerReport = () => {
  const [exporting, setExporting] = useState(false)
  const [refresher, setRefresher] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [currentReportVersion, setCurrentReportVersion] = useState()

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { DESCRIPTION, REPORT_TYPES, EXPORT_FORMAT_TYPES } = constants.reports

  const formMethods = useForm({
    validationSchema: schema(),
    defaultValues: {
      exportFormat: EXPORT_FORMAT_TYPES.PDF,
      dataAction: constants.reportVersions.DESTROY_VERSION_ID_ACTION,
    },
  })

  const {
    control,
    watch,
    errors,
    clearError,
    setError,
    triggerValidation,
    setValue,
  } = formMethods

  const reportSelected = watch('reportSelected')
  const partnerIdField = watch('partnerId')
  const auditIdField = watch('auditId')

  const isAssessment = [
    REPORT_TYPES.SINGLE_ASSESSMENT,
    REPORT_TYPES.ALL_ASSESSMENTS,
  ].includes(reportSelected)
  const isReportByAll = [
    REPORT_TYPES.ALL_ASSESSMENTS,
    REPORT_TYPES.ALL_PARTNERS,
  ].includes(reportSelected)

  const handleRefresh = () => setRefresher((prev) => !prev)

  const mixPanel = (action) => {
    helpers.mixpanel.track('Relatório de Fornecedores', 'Gerar Relatório', {
      action,
    })
  }

  const triggerMixPanelAction = () => {
    if (reportSelected === REPORT_TYPES.ALL_PARTNERS) {
      mixPanel('gerar-relatorio-fornecedores')
    } else if (reportSelected === REPORT_TYPES.SINGLE_PARTNER) {
      mixPanel('gerar-relatorio-fornecedor')
    } else if (reportSelected === REPORT_TYPES.ALL_ASSESSMENTS) {
      mixPanel('gerar-relatorio-pre-avaliacoes')
    } else {
      mixPanel('gerar-relatorio-pre-avaliacao')
    }
  }

  // Nos tipos "todos" o backend separa fornecedor de pré-avaliado pelo reportType.
  const buildExportParams = (shouldCreateVersion, exportFormat) => {
    return {
      partnerIds: isReportByAll ? undefined : [partnerIdField?.id],
      companySupplierAuditId: auditIdField?.id,
      reportType: reportSelected,
      exportFormat,
      shouldCreateVersion,
    }
  }

  const exportReport = async (shouldCreateVersion, exportFormat) => {
    setExporting(true)
    try {
      triggerMixPanelAction()

      await service.dponet.suppliers.partnerExportReport(
        buildExportParams(shouldCreateVersion, exportFormat),
      )

      const isExcel = exportFormat === EXPORT_FORMAT_TYPES.EXCEL

      snackbar.open({
        message: `O relatório ${
          isExcel ? 'Excel ' : ''
        }será gerado e enviado ao seu e-mail. Pode demorar alguns minutos.`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            'Houve um erro durante a criação do relatório.',
        ),
        variant: 'error',
      })
    } finally {
      setExporting(false)
      setTimeout(handleRefresh, 8000)
    }
  }

  const handleCloseExportFormatDialog = () => setDialogOpen(false)

  const handleOpenExportFormatDialog = async () => {
    const isValid = await triggerValidation()
    if (!isValid) return

    setDialogOpen(true)
  }

  const onConfirmExport = async (data) => {
    const hasVersion = !!currentReportVersion?.id

    if (hasVersion && !data?.dataAction) {
      setError(
        'dataAction',
        'required',
        'Selecione se deseja apenas baixar ou gerar uma nova versão.',
      )
      return
    }
    clearError('dataAction')
    handleCloseExportFormatDialog()

    const isDestroyAction =
      data?.dataAction === constants.reportVersions.DESTROY_VERSION_ID_ACTION

    if (isDestroyAction && hasVersion) {
      try {
        await service.dponet.reportVersions.destroy({
          reportVersionId: currentReportVersion.id,
        })
        setCurrentReportVersion(null)
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error ||
              'Falha ao remover versão do relatório.',
          ),
          variant: 'error',
        })
      }
    }

    const shouldCreateVersion = !hasVersion || isDestroyAction

    await exportReport(shouldCreateVersion, data.exportFormat)
  }

  useEffect(() => {
    if (partnerIdField) {
      setValue('partnerId', null)
    }
    if (auditIdField) {
      setValue('auditId', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSelected])

  return (
    <FormContext {...formMethods}>
      <Page className={classes.root} title="Relatório de Fornecedores">
        <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
          <Box py={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Header
                  type="partner"
                  reportTour="partner"
                  buttonAction={handleOpenExportFormatDialog}
                  disabled={exporting}
                  title={DESCRIPTION.partner.title}
                  description={DESCRIPTION.partner.content}
                  buttonType="button"
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel className={classes.label}>
                  Escolha a versão do relatório:
                </InputLabel>
                <PartnerReportSelection
                  control={control}
                  errors={errors}
                  clearError={clearError}
                />
              </Grid>
              {!isReportByAll && (
                <>
                  <Grid item xs={12}>
                    <PartnerForm
                      key={reportSelected}
                      errors={errors}
                      preAvaliation={isAssessment}
                      label={isAssessment ? 'Pré-Avaliado' : 'Fornecedor'}
                    />
                  </Grid>
                  {partnerIdField?.companySupplierId && (
                    <Grid item xs={12}>
                      <PartnerAuditForm
                        key={partnerIdField.companySupplierId}
                        control={control}
                        errors={errors}
                        companySupplierId={partnerIdField.companySupplierId}
                        setValue={setValue}
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.PARTNER}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
          </Box>

          <ExportFormatDialog
            open={dialogOpen}
            onClose={handleCloseExportFormatDialog}
            onConfirmExport={formMethods.handleSubmit(onConfirmExport)}
            isLoadingExcel={exporting}
            currentReportVersion={currentReportVersion}
          />
        </Container>
      </Page>
    </FormContext>
  )
}
export default PartnerReport
